import React, { createContext, useContext, useEffect, useState } from "react";
import usersData from "../users.json";

type Role = "user" | "admin";

interface User {
  username: string;
  role: Role;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem("authUser");
      return stored ? (JSON.parse(stored) as User) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) localStorage.setItem("authUser", JSON.stringify(user));
    else localStorage.removeItem("authUser");
  }, [user]);

  const login = async (username: string, password: string) => {
    // Try server-side login first
    try {
      const res = await fetch(`/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        const data = await res.json();
        const u: User = { username: data.user.username, role: data.user.role as Role };
        setUser(u);
        try {
          localStorage.setItem("authToken", data.token);
        } catch { }
        return true;
      }
    } catch {
      // network error - fall through to local fallback
    }

    // Fallback: local users.json (offline/dev)
    const found = (usersData as any[]).find((u) => u.username === username && u.password === password);
    if (found) {
      const u: User = { username: found.username, role: found.role as Role };
      setUser(u);
      try {
        localStorage.removeItem("authToken");
      } catch { }
      return true;
    }

    return false;
  };

  const logout = () => {
    try {
      const token = localStorage.getItem("authToken");
      if (token) fetch(`/api/auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => { });
    } catch { }
    try {
      localStorage.removeItem("authToken");
    } catch { }
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
